import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import pasteImage from "../../../../assets/images/svgs/paste.png";
import deleteImage from "../../../../assets/images/svgs/recycleBin.png";
import cutImage from "../../../../assets/images/svgs/scissors.png";
import AddEntityIcon from "../../../../components/icons/AddEntityIcon";
import AddRoomInformationIcon from "../../../../components/icons/AddRoomInformationIcon";
import AddRoomTimeIcon from "../../../../components/icons/AddRoomTimeIcon";
import DeleteActionIcon from "../../../../components/icons/DeleteActionIcon";
import EditRoomInformationIcon from "../../../../components/icons/EditRoomInformationIcon";
import NotificationIcon from "../../../../components/icons/NotificationIcon";
import NotificationSentIcon from "../../../../components/icons/NotificationSentIcon";
import RefreshIcon from "../../../../components/icons/RefreshIcon";
import ShowAllEntriesIcon from "../../../../components/icons/ShowAllEntriesIcon";
import TransferIcon from "../../../../components/icons/TransferIcon";
import TextInput from "../../../../components/inputs/textInput/TextInput";
import Loader from "../../../../components/loader/Loader";
import Dialog from "../../../../components/dialog/Dialog";
import DialogButton from "../../../../components/dialog/DialogButton";
import Portal from "../../../../components/portal/Portal";
import SelectionBox from "../../../../components/selectionBox/SelectionBox";
import TimelineTable from "../../../../components/timelineTable/TimelineTable";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import ChevronDownIcon from "../../../../components/icons/ChevronDownIcon";
import useStoredFlag from "../../../../hooks/useStoredFlag";
import { useTheme } from "../../../../providers/ThemeProvider";
import { fetchAdmins } from "../../../../features/admins/adminsSlice";
import { showConfirmationModal } from "../../../../features/confirmationModal/ConfirmationModalSlice";
import { fetchControlledSetNumbers } from "../../../../features/controlledSetNumbers/controlledSetNumbersSlice";
import {
  clearCutPlayers,
  fetchTournamentEntriesByTableIds,
  moveEntryToFinalStage,
  moveEntryToNextStage,
  movePlayers,
  resetHighlights,
  resetSelectedPlayers,
  setCutPlayers,
  setSelectedPlayers,
} from "../../../../features/players/playersSlice";
import {
  createNextStageTables,
  fetchTournamentTablesById,
  syncTournamentTablesById,
} from "../../../../features/tables/tableSlice";
import { fetchTimelineSetByTournamentId } from "../../../../features/timelines/timelinesSlice";
import useContextMenu from "../../../../hooks/useContextMenu";
import useSelectionBox from "../../../../hooks/useSelectionBox";
import useVisibilityObserver from "../../../../hooks/useVisibilityObserver";
import OneTable from "../oneTournamentTables/oneTable/OneTable";
import styles from "./oneTournamentTables.module.css";
import TransferModal from "./transferModal/TransferModal";
import { isSetExecutorRole } from "../../../../constants/roleRules";

const tableTypeOptions = [
  { value: "all", label: "كل الانواع" },
  { value: "qualification", label: "تصفيات" },
  { value: "nextStage", label: "متقدمة" },
  { value: "final", label: "نهائيات" },
  { value: "winners", label: "الفائزين" },
  { value: "top2", label: "المركز الثاني" },
  { value: "top3", label: "المركز الثالث" },
];
// Only with view-winner-tables (the backend leaves these tables out otherwise).
const WINNER_TABLE_TYPES = new Set(["winners", "top2", "top3"]);

// A multi-select as chips: "all" clears the choice; each chip toggles one value.
// options = [{ value, label }], selected = the chosen values (strings).
const FilterChips = ({ allLabel, options, selected, onChange }) => (
  <div className={styles.filterChips}>
    <button
      type="button"
      className={`${styles.filterChip} ${selected.length === 0 ? styles.filterChipActive : ""}`}
      aria-pressed={selected.length === 0}
      onClick={() => onChange([])}
    >
      {allLabel}
    </button>
    {options.map((option) => {
      const isOn = selected.includes(option.value);
      return (
        <button
          key={option.value}
          type="button"
          className={`${styles.filterChip} ${isOn ? styles.filterChipActive : ""}`}
          aria-pressed={isOn}
          onClick={() =>
            onChange(
              isOn
                ? selected.filter((value) => value !== option.value)
                : [...selected, option.value],
            )
          }
        >
          {option.label}
        </button>
      );
    })}
  </div>
);
const TABLE_ENTRIES_BATCH_SIZE = 25;
const TABLE_ENTRIES_BATCH_CONCURRENCY = 3;
const DEFAULT_NEXT_STAGE_TABLE_COUNT = 15;
const SET_NUMBER_COLOR_PALETTE_SIZE = 50;
const DEFAULT_TIMELINE_SPLIT_PERCENT = 50;
const MIN_TIMELINE_SPLIT_PERCENT = 18;
const MAX_TIMELINE_SPLIT_PERCENT = 82;
const TIMELINE_SPLIT_PERCENT_STORAGE_KEY =
  "oneTournamentTables.timelineSplitPercent";
const TIMELINE_SPLIT_VIEW_STORAGE_KEY =
  "oneTournamentTables.timelineSplitViewEnabled";
const TIMELINE_SPLIT_DIVIDER_WIDTH_PX = 18;
const SET_NUMBER_COLOR_HUES = [210, 156, 42, 254, 188, 24, 222, 92, 30, 238];

const buildSetNumberColorPalette = ({
  size = SET_NUMBER_COLOR_PALETTE_SIZE,
  isDark = false,
} = {}) =>
  Array.from({ length: size }, (_, index) => {
    const hue = SET_NUMBER_COLOR_HUES[index % SET_NUMBER_COLOR_HUES.length];
    const toneShift =
      Math.floor(index / SET_NUMBER_COLOR_HUES.length) % 4;

    if (isDark) {
      return {
        backgroundColor: `hsl(${hue}, ${6 + toneShift}%, ${13 + toneShift}%)`,
        borderColor: `hsla(${hue}, ${28 + toneShift * 3}%, ${52 - toneShift * 2}%, 0.3)`,
        textColor: `hsl(${hue}, ${12 + toneShift}%, ${86 - toneShift}%)`,
      };
    }

    return {
      backgroundColor: `hsl(${hue}, ${54 + toneShift * 4}%, ${88 - toneShift * 2}%)`,
      borderColor: `hsl(${hue}, ${52 + toneShift * 4}%, ${44 - toneShift * 3}%)`,
      textColor: `hsl(${hue}, 24%, ${24 + toneShift}%)`,
    };
  });

const getSetNumberColorTheme = (setNumberValue, palette) => {
  const normalizedSetNumber = String(setNumberValue ?? "").trim();
  if (!normalizedSetNumber || normalizedSetNumber === "__unset__") return null;

  const paletteSize = palette.length;
  if (!paletteSize) return null;

  const asNumber = Number.parseInt(normalizedSetNumber, 10);
  if (Number.isFinite(asNumber)) {
    const numericIndex = ((asNumber % paletteSize) + paletteSize) % paletteSize;
    return palette[numericIndex];
  }

  let hash = 0;
  for (let i = 0; i < normalizedSetNumber.length; i += 1) {
    hash =
      (hash * 31 + normalizedSetNumber.charCodeAt(i)) % Number.MAX_SAFE_INTEGER;
  }
  const hashedIndex = ((hash % paletteSize) + paletteSize) % paletteSize;
  return palette[hashedIndex];
};

const allocateLargestRemainder = (rows = [], targetTotal = 0) => {
  const normalizedTarget = Math.max(0, Number(targetTotal) || 0);
  const normalizedRows = rows.map((row) => ({
    tableId: String(row?.tableId || ""),
    snapshotEntries: Math.max(0, Number(row?.snapshotEntries) || 0),
  }));

  const totalSnapshot = normalizedRows.reduce(
    (sum, row) => sum + row.snapshotEntries,
    0,
  );

  if (!normalizedRows.length || normalizedTarget <= 0 || totalSnapshot <= 0) {
    return normalizedRows.map((row) => ({
      ...row,
      quotaEntries: 0,
    }));
  }

  const quotaRows = normalizedRows.map((row) => {
    const exactQuota = (row.snapshotEntries / totalSnapshot) * normalizedTarget;
    return {
      ...row,
      quotaEntries: Math.floor(exactQuota),
      remainder: exactQuota - Math.floor(exactQuota),
    };
  });

  let remaining =
    normalizedTarget -
    quotaRows.reduce((sum, row) => sum + row.quotaEntries, 0);

  const sorted = [...quotaRows].sort((a, b) => {
    if (b.remainder !== a.remainder) return b.remainder - a.remainder;
    if (b.snapshotEntries !== a.snapshotEntries) {
      return b.snapshotEntries - a.snapshotEntries;
    }
    return a.tableId.localeCompare(b.tableId);
  });

  for (let i = 0; i < sorted.length && remaining > 0; i += 1) {
    sorted[i].quotaEntries += 1;
    remaining -= 1;
  }

  const byId = new Map(sorted.map((row) => [row.tableId, row.quotaEntries]));

  return normalizedRows.map((row) => ({
    ...row,
    quotaEntries: byId.get(row.tableId) || 0,
  }));
};

const toDateTimeLocalValue = (timestamp) => {
  if (!Number.isFinite(timestamp)) return "";
  const date = new Date(timestamp);
  const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
};

const toTimestamp = (value) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const toMinuteTimestamp = (value) => {
  const timestamp = toTimestamp(value);
  if (timestamp === null) return null;
  return Math.floor(timestamp / 60000) * 60000;
};

const getSharedTimeGroupKey = (table) => {
  if (table?.tableType !== "qualification") return null;
  const slotStartMs = toMinuteTimestamp(table?.timeline?.slotStart);
  if (slotStartMs === null) return null;
  return String(slotStartMs);
};

const formatSharedTimeLabel = (value, timeZone = "Asia/Amman") => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const clampTimelineSplitPercent = (value) =>
  Math.min(
    MAX_TIMELINE_SPLIT_PERCENT,
    Math.max(MIN_TIMELINE_SPLIT_PERCENT, Number(value) || 0),
  );

const readStoredTimelineSplitPercent = () => {
  if (typeof window === "undefined") return DEFAULT_TIMELINE_SPLIT_PERCENT;

  try {
    const rawValue = window.localStorage.getItem(
      TIMELINE_SPLIT_PERCENT_STORAGE_KEY,
    );
    const parsedValue = Number.parseFloat(rawValue || "");
    if (!Number.isFinite(parsedValue)) {
      return DEFAULT_TIMELINE_SPLIT_PERCENT;
    }

    return clampTimelineSplitPercent(parsedValue);
  } catch (_error) {
    return DEFAULT_TIMELINE_SPLIT_PERCENT;
  }
};

const readStoredTimelineSplitViewEnabled = () => {
  if (typeof window === "undefined") return false;

  try {
    return (
      window.localStorage.getItem(TIMELINE_SPLIT_VIEW_STORAGE_KEY) === "true"
    );
  } catch (_error) {
    return false;
  }
};

const OneTournamentTables = () => {
  const { isDark } = useTheme();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showNextStageSetupModal, setShowNextStageSetupModal] = useState(false);
  const [nextStageTargetTableCount, setNextStageTargetTableCount] =
    useState(15);
  const [nextStagePlayersLimit, setNextStagePlayersLimit] = useState(60);
  const [showFinalTableModal, setShowFinalTableModal] = useState(false);
  const [finalTablePlayersLimit, setFinalTablePlayersLimit] = useState(60);
  const [query, setQuery] = useState("");
  // Several table types / columns at once; empty = all.
  const [tableTypeFilter, setTableTypeFilter] = useState([]);
  const [setNumberFilter, setSetNumberFilter] = useState([]);
  const [notFullOnlyFilter, setNotFullOnlyFilter] = useState(false);
  const [withoutNotificationOnlyFilter, setWithoutNotificationOnlyFilter] =
    useState(false);
  const [timeRangeStart, setTimeRangeStart] = useState("");
  const [timeRangeEnd, setTimeRangeEnd] = useState("");
  const [isTimeRangeFilterApplied, setIsTimeRangeFilterApplied] =
    useState(false);
  const [persistedSetNumberOptions, setPersistedSetNumberOptions] = useState(
    [],
  );
  const [isTimelineSplitView, setIsTimelineSplitView] = useState(() =>
    readStoredTimelineSplitViewEnabled(),
  );
  const [timelineSplitPercent, setTimelineSplitPercent] = useState(() =>
    readStoredTimelineSplitPercent(),
  );
  const { tournamentId } = useParams();
  const dispatch = useDispatch();
  const didInitialScrollRef = useRef(false);
  const fetchedTableIdsRef = useRef(new Set());
  const splitViewRef = useRef(null);
  const dividerDragStateRef = useRef({ active: false });
  const setNumberColorPalette = useMemo(
    () => buildSetNumberColorPalette({ isDark }),
    [isDark],
  );

  const location = useLocation();
  const name = location.state?.name;
  const gameName = location.state?.gameName;
  const isPubgTournament = String(gameName || "").toLowerCase() === "pubg";

  const {
    list: tables,
    fetchStatus,
    syncStatus,
    addStatus,
    createNextStageStatus,
    syncCursorsByKey,
    lastSyncDelta,
    nextStagePlanByTournament,
  } = useSelector((state) => state.tables);
  const players = useSelector((state) => state.players.data);
  const status = useSelector((state) => state.players.status);
  const { rolePermissions } = useSelector((state) => state.user);
  // Adding, moving and removing players (context menu, transfer, drag).
  const canManagePlayers = rolePermissions.includes("update-tables");
  const canCreateTables = rolePermissions.includes("create-tables");
  const canSetupNextStage = rolePermissions.includes("setup-next-stage");
  const canSeeWinnerTables = rolePermissions.includes("view-winner-tables");
  // The toolbar above the tables (create, filters, search, actions) folds
  // away to give the tables the room; remembered like the sidebar.
  const [isHeaderCollapsed, toggleHeaderCollapsed] = useStoredFlag(
    "oneTournamentTables.headerCollapsed",
  );
  const visibleTableTypeOptions = useMemo(
    () =>
      canSeeWinnerTables
        ? tableTypeOptions
        : tableTypeOptions.filter((option) => !WINNER_TABLE_TYPES.has(option.value)),
    [canSeeWinnerTables],
  );
  const { timeZone } = useSelector((state) => state.timeZone);
  const { cutPlayers } = useSelector((state) => state.players);
  const { list: admins, fetchStatus: adminsFetchStatus } = useSelector(
    (state) => state.admins,
  );
  const { controlledSetNumbers } = useSelector(
    (state) => state.controlledSetNumbers,
  );
  const {
    cache: timelineCache,
    status: timelinesStatus,
    error: timelinesError,
  } = useSelector((state) => state.timelines);
  const tournamentControlledSetNumbers =
    controlledSetNumbers?.[String(tournamentId)] || null;
  const normalizedTournamentId = String(tournamentId || "");
  const hasTimelineCache = Object.prototype.hasOwnProperty.call(
    timelineCache || {},
    normalizedTournamentId,
  );
  const timelineData = hasTimelineCache
    ? timelineCache?.[normalizedTournamentId] || []
    : [];
  const isTimelineLoading =
    isTimelineSplitView &&
    !hasTimelineCache &&
    timelinesStatus !== "failed";

  const hasSquadEntries = useMemo(
    () =>
      Object.values(players || {}).some((rows) =>
        (Array.isArray(rows) ? rows : []).some(
          (entry) => entry?.kind === "squad",
        ),
      ),
    [players],
  );

  const tournamentType = useMemo(() => {
    if (location.state?.tournamentType === "squad") return "squad";
    if (location.state?.tournamentType === "solo") return "solo";

    const parsedMaxUsersByTeam = Number.parseInt(
      location.state?.maxUsersByTeam,
      10,
    );
    if (Number.isFinite(parsedMaxUsersByTeam) && parsedMaxUsersByTeam > 1) {
      return "squad";
    }

    if (hasSquadEntries) return "squad";
    return "solo";
  }, [
    hasSquadEntries,
    location.state?.maxUsersByTeam,
    location.state?.tournamentType,
  ]);

  const maxUsersByTeam = Math.max(
    1,
    Number.parseInt(location.state?.maxUsersByTeam, 10) ||
      (tournamentType === "squad" ? 4 : 1),
  );

  const containerRef = useRef(null);
  const { visibleIds, observe } = useVisibilityObserver(containerRef);
  const nextStagePlan =
    nextStagePlanByTournament?.[String(tournamentId)] || null;

  const resolvedNextStagePlan = useMemo(() => {
    if (nextStagePlan) return nextStagePlan;

    const qualificationTables = tables.filter(
      (table) => table?.tableType === "qualification",
    );
    if (!qualificationTables.length) return null;

    const playersLimitPerTable = Math.max(
      1,
      Number(
        tables.find((table) => table?.tableType === "nextStage")
          ?.playersLimit || (isPubgTournament ? 100 : 60),
      ) || (isPubgTournament ? 100 : 60),
    );

    const entriesPerTable =
      isPubgTournament && tournamentType === "squad"
        ? 25
        : tournamentType === "squad"
          ? Math.max(
              1,
              Math.floor(playersLimitPerTable / Math.max(2, maxUsersByTeam)),
            )
          : playersLimitPerTable;

    const quotasInput = qualificationTables.map((table) => ({
      tableId: String(table?._id || ""),
      snapshotEntries: (players[String(table?._id || "")] || []).length,
    }));

    const totalQualificationEntriesSnapshot = quotasInput.reduce(
      (sum, item) => sum + item.snapshotEntries,
      0,
    );
    const requestedTotalEntries =
      DEFAULT_NEXT_STAGE_TABLE_COUNT * entriesPerTable;
    const effectiveTotalEntries = Math.min(
      requestedTotalEntries,
      totalQualificationEntriesSnapshot,
    );

    const quotas = allocateLargestRemainder(quotasInput, effectiveTotalEntries);
    const movedEntriesByTable = quotas.reduce((acc, row) => {
      acc[row.tableId] = 0;
      return acc;
    }, {});

    return {
      targetTableCount: DEFAULT_NEXT_STAGE_TABLE_COUNT,
      playersLimitPerTable,
      entriesPerTable,
      requestedTotalEntries,
      effectiveTotalEntries,
      totalQualificationEntriesSnapshot,
      quotas,
      movedEntriesByTable,
    };
  }, [
    isPubgTournament,
    maxUsersByTeam,
    nextStagePlan,
    players,
    tables,
    tournamentType,
  ]);

  useEffect(() => {
    didInitialScrollRef.current = false;
    fetchedTableIdsRef.current = new Set();
    setPersistedSetNumberOptions([]);
  }, [tournamentId]);

  useEffect(() => {
    didInitialScrollRef.current = false;
  }, [isTimelineSplitView]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(
        TIMELINE_SPLIT_PERCENT_STORAGE_KEY,
        String(clampTimelineSplitPercent(timelineSplitPercent)),
      );
    } catch (_error) {
      // Ignore storage failures and keep the in-memory value.
    }
  }, [timelineSplitPercent]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(
        TIMELINE_SPLIT_VIEW_STORAGE_KEY,
        String(Boolean(isTimelineSplitView)),
      );
    } catch (_error) {
      // Ignore storage failures and keep the in-memory value.
    }
  }, [isTimelineSplitView]);

  useEffect(() => {
    setTableTypeFilter([]);
    setSetNumberFilter([]);
    setNotFullOnlyFilter(false);
    setWithoutNotificationOnlyFilter(false);
    setTimeRangeStart("");
    setTimeRangeEnd("");
    setIsTimeRangeFilterApplied(false);
    setQuery("");
    setShowNextStageSetupModal(false);
    setNextStageTargetTableCount(15);
    setNextStagePlayersLimit(isPubgTournament ? 100 : 60);
    setShowFinalTableModal(false);
    setFinalTablePlayersLimit(60);
  }, [isPubgTournament, tournamentId]);

  useEffect(() => {
    if (
      adminsFetchStatus === "idle" &&
      rolePermissions.includes("view-admins")
    ) {
      dispatch(fetchAdmins());
    }
  }, [adminsFetchStatus, dispatch, rolePermissions]);

  useEffect(() => {
    if (!tournamentId) return;
    if (tournamentControlledSetNumbers) return;
    dispatch(fetchControlledSetNumbers(tournamentId));
  }, [dispatch, tournamentId, tournamentControlledSetNumbers]);

  useEffect(() => {
    if (!isTimelineSplitView || !tournamentId || hasTimelineCache) return;
    dispatch(fetchTimelineSetByTournamentId(tournamentId));
  }, [dispatch, hasTimelineCache, isTimelineSplitView, tournamentId]);

  useEffect(() => {
    if (!isTimelineSplitView) return undefined;

    const handlePointerMove = (event) => {
      if (!dividerDragStateRef.current.active) return;

      const container = splitViewRef.current;
      if (!container) return;

      const bounds = container.getBoundingClientRect();
      if (!bounds.width) return;

      const nextPercent = clampTimelineSplitPercent(
        ((event.clientX - bounds.left) / bounds.width) * 100,
      );
      setTimelineSplitPercent(nextPercent);
    };

    const handlePointerRelease = () => {
      if (!dividerDragStateRef.current.active) return;
      dividerDragStateRef.current.active = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerRelease);
    window.addEventListener("pointercancel", handlePointerRelease);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerRelease);
      window.removeEventListener("pointercancel", handlePointerRelease);
      handlePointerRelease();
    };
  }, [isTimelineSplitView]);

  useEffect(() => {
    if (!tables.length || didInitialScrollRef.current) return;
    const container = containerRef.current;
    if (!container) return;

    const scrollToRight = () => {
      const maxScrollLeft = Math.max(
        0,
        container.scrollWidth - container.clientWidth,
      );

      container.scrollLeft = maxScrollLeft;

      // RTL fallback for browsers using negative scrollLeft.
      if (container.scrollLeft === 0 && maxScrollLeft > 0) {
        container.scrollLeft = -maxScrollLeft;
      }
    };

    requestAnimationFrame(() => {
      scrollToRight();
      didInitialScrollRef.current = true;
    });
  }, [isTimelineSplitView, tables.length]);

  useEffect(() => {
    if (!tables.length) return;

    const fetchedSetNumbers = [
      ...new Set(
        tables
          .map((table) => Number(table?.timeline?.setNumber))
          .filter((num) => Number.isFinite(num)),
      ),
    ].sort((a, b) => a - b);

    if (!fetchedSetNumbers.length) return;

    setPersistedSetNumberOptions((prev) => {
      const merged = [...new Set([...prev, ...fetchedSetNumbers])].sort(
        (a, b) => a - b,
      );

      if (
        merged.length === prev.length &&
        merged.every((value, index) => value === prev[index])
      ) {
        return prev;
      }

      return merged;
    });
  }, [tables]);
  const timelineMinStartMs = useMemo(() => {
    const starts = tables
      .map((table) => toTimestamp(table?.timeline?.slotStart))
      .filter((value) => value !== null);
    if (!starts.length) return null;
    return Math.min(...starts);
  }, [tables]);

  const timelineMaxEndMs = useMemo(() => {
    const ends = tables
      .map((table) => toTimestamp(table?.timeline?.slotEnd))
      .filter((value) => value !== null);
    if (!ends.length) return null;
    return Math.max(...ends);
  }, [tables]);

  const defaultRangeStartValue = useMemo(
    () => (timelineMinStartMs ? toDateTimeLocalValue(timelineMinStartMs) : ""),
    [timelineMinStartMs],
  );
  const defaultRangeEndValue = useMemo(
    () => (timelineMaxEndMs ? toDateTimeLocalValue(timelineMaxEndMs) : ""),
    [timelineMaxEndMs],
  );

  useEffect(() => {
    if (!timelineMinStartMs || !timelineMaxEndMs) return;
    setTimeRangeStart(
      (prev) => prev || toDateTimeLocalValue(timelineMinStartMs),
    );
    setTimeRangeEnd((prev) => prev || toDateTimeLocalValue(timelineMaxEndMs));
  }, [timelineMinStartMs, timelineMaxEndMs]);

  const selectedRangeStartMs = useMemo(
    () => toMinuteTimestamp(timeRangeStart),
    [timeRangeStart],
  );
  const selectedRangeEndMs = useMemo(
    () => toMinuteTimestamp(timeRangeEnd),
    [timeRangeEnd],
  );

  const hasActiveTimeFilter = useMemo(() => {
    if (!isTimeRangeFilterApplied) return false;
    if (!timeRangeStart || !timeRangeEnd) return false;
    return true;
  }, [isTimeRangeFilterApplied, timeRangeStart, timeRangeEnd]);

  const fetchFilters = useMemo(() => {
    const filters = {};

    if (tableTypeFilter.length) {
      filters.tableType = tableTypeFilter.join(",");
    }

    if (setNumberFilter.length) {
      filters.setNumber = setNumberFilter.join(",");
    }

    if (notFullOnlyFilter) {
      filters.notFullOnly = true;
    }

    if (withoutNotificationOnlyFilter) {
      filters.withoutNotificationOnly = true;
    }

    if (
      hasActiveTimeFilter &&
      selectedRangeStartMs !== null &&
      selectedRangeEndMs !== null
    ) {
      filters.slotStartFrom = new Date(selectedRangeStartMs).toISOString();
      filters.slotEndTo = new Date(selectedRangeEndMs).toISOString();
    }

    return filters;
  }, [
    tableTypeFilter,
    setNumberFilter,
    notFullOnlyFilter,
    withoutNotificationOnlyFilter,
    hasActiveTimeFilter,
    selectedRangeStartMs,
    selectedRangeEndMs,
  ]);

  const fetchFiltersSerialized = useMemo(
    () => JSON.stringify(fetchFilters),
    [fetchFilters],
  );
  const syncKey = useMemo(
    () => `${tournamentId}::${fetchFiltersSerialized}`,
    [tournamentId, fetchFiltersSerialized],
  );

  useEffect(() => {
    const normalizedFilters = JSON.parse(fetchFiltersSerialized);
    dispatch(
      fetchTournamentTablesById({
        tournamentId,
        filters: normalizedFilters,
        silent: true,
        syncKey,
      }),
    );
  }, [dispatch, tournamentId, fetchFiltersSerialized, syncKey]);

  const fetchEntriesInBatches = useCallback(
    async (tableIds, { silent = true } = {}) => {
      const normalizedTableIds = [
        ...new Set(
          (Array.isArray(tableIds) ? tableIds : [])
            .map((tableId) => String(tableId || "").trim())
            .filter(Boolean),
        ),
      ];

      if (!normalizedTableIds.length) return;

      const chunks = [];
      for (
        let i = 0;
        i < normalizedTableIds.length;
        i += TABLE_ENTRIES_BATCH_SIZE
      ) {
        chunks.push(normalizedTableIds.slice(i, i + TABLE_ENTRIES_BATCH_SIZE));
      }

      for (let i = 0; i < chunks.length; i += TABLE_ENTRIES_BATCH_CONCURRENCY) {
        const windowChunks = chunks.slice(
          i,
          i + TABLE_ENTRIES_BATCH_CONCURRENCY,
        );
        await Promise.all(
          windowChunks.map((chunkTableIds) =>
            dispatch(
              fetchTournamentEntriesByTableIds({
                tournamentId,
                tableIds: chunkTableIds,
                silent,
              }),
            ),
          ),
        );
      }
    },
    [dispatch, tournamentId],
  );

  useEffect(() => {
    if (!tables.length) return;

    const missingTableIds = tables
      .map((table) => table?._id)
      .filter((tableId) => {
        const normalizedTableId = String(tableId || "");
        if (!normalizedTableId) return false;
        if (fetchedTableIdsRef.current.has(normalizedTableId)) return false;
        fetchedTableIdsRef.current.add(normalizedTableId);
        return true;
      });

    if (!missingTableIds.length) return;

    void fetchEntriesInBatches(missingTableIds, { silent: true });
  }, [fetchEntriesInBatches, tables]);

  useEffect(() => {
    if (!lastSyncDelta) return;
    if (lastSyncDelta.mode !== "delta") return;
    if (lastSyncDelta.syncKey !== syncKey) return;

    const changedTableIds = (lastSyncDelta.changedTableIds || [])
      .map((id) => String(id || ""))
      .filter(Boolean);
    const removedTableIds = (lastSyncDelta.removedTableIds || [])
      .map((id) => String(id || ""))
      .filter(Boolean);

    if (!changedTableIds.length && !removedTableIds.length) return;

    removedTableIds.forEach((tableId) => {
      fetchedTableIdsRef.current.delete(tableId);
    });

    const activeTableIdSet = new Set(
      tables.map((table) => String(table?._id || "")),
    );

    changedTableIds.forEach((tableId) => {
      if (!activeTableIdSet.has(tableId)) return;
      fetchedTableIdsRef.current.delete(tableId);
    });

    const changedVisibleTableIds = changedTableIds.filter((tableId) =>
      activeTableIdSet.has(tableId),
    );
    if (changedVisibleTableIds.length) {
      void fetchEntriesInBatches(changedVisibleTableIds, { silent: true });
    }
  }, [fetchEntriesInBatches, lastSyncDelta, syncKey, tables]);

  const tablesByFilter = useMemo(() => {
    return tables.filter((table) => {
      if (tableTypeFilter.length && !tableTypeFilter.includes(table?.tableType)) {
        return false;
      }

      if (
        setNumberFilter.length &&
        !setNumberFilter.includes(String(table?.timeline?.setNumber))
      ) {
        return false;
      }

      if (notFullOnlyFilter && table?.isFull) {
        return false;
      }

      if (
        withoutNotificationOnlyFilter &&
        table?.latestNotification?.createdAt
      ) {
        return false;
      }

      if (
        hasActiveTimeFilter &&
        selectedRangeStartMs !== null &&
        selectedRangeEndMs !== null
      ) {
        const tableStartMs = toMinuteTimestamp(table?.timeline?.slotStart);
        const tableEndMs = toMinuteTimestamp(table?.timeline?.slotEnd);

        if (tableStartMs === null || tableEndMs === null) {
          return false;
        }

        if (
          tableStartMs < selectedRangeStartMs ||
          tableEndMs > selectedRangeEndMs
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    tables,
    tableTypeFilter,
    setNumberFilter,
    notFullOnlyFilter,
    withoutNotificationOnlyFilter,
    selectedRangeStartMs,
    selectedRangeEndMs,
    hasActiveTimeFilter,
  ]);

  const findMatchingTableIds = (candidateTables) => {
    if (!query.trim()) return candidateTables.map((t) => t?._id);

    const candidateIds = new Set(
      candidateTables.map((table) => String(table?._id)),
    );
    const lowerQuery = query.toLowerCase();

    return Object.entries(players)
      .filter(([key, array]) => {
        if (!candidateIds.has(String(key))) return false;

        return array.some((item) => {
          const directMatch =
            item.gameId?.toLowerCase().includes(lowerQuery) ||
            item.gameName?.toLowerCase().includes(lowerQuery) ||
            item.name?.toLowerCase().includes(lowerQuery) ||
            item.squadSlug?.toLowerCase().includes(lowerQuery);

          if (directMatch) return true;

          if (item.kind !== "squad") return false;

          return (item.members || []).some(
            (member) =>
              member?.name?.toLowerCase().includes(lowerQuery) ||
              member?.gameName?.toLowerCase().includes(lowerQuery) ||
              member?.gameId?.toLowerCase().includes(lowerQuery),
          );
        });
      })
      .map(([key]) => key);
  };

  const matchingIds = findMatchingTableIds(tablesByFilter);
  const matchingIdSet = useMemo(
    () => new Set(matchingIds.map((id) => String(id))),
    [matchingIds],
  );
  const filteredTables = tablesByFilter.filter((t) =>
    matchingIdSet.has(String(t?._id)),
  );
  const groupedTables = useMemo(() => {
    const groups = [];

    filteredTables.forEach((table, index) => {
      const sharedTimeKey = getSharedTimeGroupKey(table);
      const lastGroup = groups[groups.length - 1];
      const canJoinPreviousGroup =
        sharedTimeKey !== null && lastGroup?.sharedTimeKey === sharedTimeKey;

      if (canJoinPreviousGroup) {
        lastGroup.tables.push(table);
        return;
      }

      groups.push({
        id:
          sharedTimeKey !== null
            ? `${sharedTimeKey}-${groups.length}`
            : `table-${String(table?._id || index)}`,
        timelineRowNumber: null,
        sharedTimeKey,
        timeLabel:
          sharedTimeKey !== null
            ? formatSharedTimeLabel(table?.timeline?.slotStart, timeZone)
            : "",
        tables: [table],
      });
    });

    let visibleSharedGroupNumber = 1;
    groups.forEach((group) => {
      if (group.sharedTimeKey) {
        group.timelineRowNumber = visibleSharedGroupNumber;
        visibleSharedGroupNumber += 1;
      }
    });

    return groups;
  }, [filteredTables, timeZone]);
  const coLeaderNamesBySetNumber = useMemo(() => {
    const bySetNumber = {};
    const coLeaders = (admins || []).filter((admin) =>
      isSetExecutorRole(admin?.role?.role),
    );

    coLeaders.forEach((admin) => {
      const setNumbers = tournamentControlledSetNumbers?.[admin?._id] || [];
      setNumbers.forEach((setNumber) => {
        const normalizedSetNumber = String(setNumber ?? "").trim();
        if (!normalizedSetNumber) return;

        if (!bySetNumber[normalizedSetNumber]) {
          bySetNumber[normalizedSetNumber] = [];
        }

        if (
          admin?.name &&
          !bySetNumber[normalizedSetNumber].includes(admin.name)
        ) {
          bySetNumber[normalizedSetNumber].push(admin.name);
        }
      });
    });

    return bySetNumber;
  }, [admins, tournamentControlledSetNumbers]);

  const {
    boxStyles,
    selectedCount,
    selectedIds: selectedIdsArray,
    selectedByTable,
  } = useSelectionBox({
    containerRef,
    onSelect: (selected) => {
      dispatch(setSelectedPlayers({ selected }));
    },
    autoClearOnClick: true,
  });

  // Every context-menu item moves, cuts/pastes or removes players.
  const { contextMenu } = useContextMenu({
    shouldOpen: () => canManagePlayers,
  });

  const selectedIds = useMemo(
    () => new Set(selectedIdsArray),
    [selectedIdsArray],
  );

  const contextTable = useMemo(() => {
    const contextTableId = String(contextMenu.data?.tableId || "");
    if (!contextTableId) return null;
    return (
      tables.find((table) => String(table?._id || "") === contextTableId) ||
      null
    );
  }, [contextMenu.data?.tableId, tables]);

  const remainingNextStageNeededFromContextTable = useMemo(() => {
    if (!resolvedNextStagePlan) return null;
    const contextTableId = String(contextMenu.data?.tableId || "");
    if (!contextTableId) return null;

    const quotaRow = (resolvedNextStagePlan.quotas || []).find(
      (item) => String(item?.tableId || "") === contextTableId,
    );
    if (!quotaRow) return null;

    const movedCount = Number(
      resolvedNextStagePlan?.movedEntriesByTable?.[contextTableId] || 0,
    );
    return Math.max(0, Number(quotaRow.quotaEntries || 0) - movedCount);
  }, [contextMenu.data?.tableId, resolvedNextStagePlan]);

  const handleCreateNewTable = (type) => {
    if (type === "final") {
      setFinalTablePlayersLimit(60);
      setShowFinalTableModal(true);
      return;
    }

    const playersLimit = type === "nextStage" && isPubgTournament ? 100 : 60;

    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من انشاء جدول جديد ؟",
        data: {
          name: `مجموعة جديده ${tables.length}`,
          tableType: type,
          tournamentId,
          playersLimit,
          region: "مختلط",
        },
        actionType: "createTable",
      }),
    );
  };

  const handleConfirmFinalTableCreation = () => {
    const playersLimit = Math.max(
      1,
      Number.parseInt(finalTablePlayersLimit, 10) || 60,
    );

    setShowFinalTableModal(false);

    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من انشاء جدول نهائيات ؟",
        data: {
          name: `مجموعة جديده ${tables.length}`,
          tableType: "final",
          tournamentId,
          playersLimit,
          region: "مختلط",
        },
        actionType: "createTable",
      }),
    );
  };

  const handleCreateNextStageTables = async () => {
    const targetTableCount = Math.max(
      1,
      Number.parseInt(nextStageTargetTableCount, 10) || 15,
    );
    const playersLimit = Math.max(
      1,
      isPubgTournament ? 100 : Number.parseInt(nextStagePlayersLimit, 10) || 60,
    );

    const action = await dispatch(
      createNextStageTables({
        tournamentId,
        targetTableCount,
        playersLimit,
      }),
    );

    if (createNextStageTables.fulfilled.match(action)) {
      setShowNextStageSetupModal(false);
    }
  };

  const handleDeletePlayer = () => {
    const isSquad = contextMenu.data?.playerKind === "squad";
    dispatch(
      showConfirmationModal({
        message: isSquad
          ? "هل انت متاكد من حذف الفريق ؟"
          : "هل انت متاكد من حذف اللاعب ؟",
        data: {
          kind: isSquad ? "squad" : "user",
          userId: isSquad ? null : contextMenu.data.playerId,
          squadId: isSquad ? contextMenu.data.playerId : null,
          tableId: contextMenu.data.tableId,
          tournamentId,
        },
        actionType: "deletePlayerFromTable",
      }),
    );
  };

  const handleCut = () => {
    dispatch(setCutPlayers(selectedByTable));
  };

  const handlePaste = async () => {
    if (
      !cutPlayers ||
      Object.keys(cutPlayers).length === 0 ||
      !contextMenu.data?.tableId
    )
      return;

    const toTableId = contextMenu.data.tableId;

    // Rows cut from this same table stay where they are (nothing to move).
    const entryMoves = Object.entries(cutPlayers)
      .filter(([fromTableId]) => String(fromTableId) !== String(toTableId))
      .flatMap(([fromTableId, rowIds]) =>
        rowIds
          .map((rowId) => {
            const tableRows = players[fromTableId] || [];
            const row = tableRows.find(
              (item) => String(item?.rowId || item?._id) === String(rowId),
            );
            if (!row) return null;

            if (row.kind === "squad") {
              return {
                kind: "squad",
                squadId: row.squadId || row._id,
                fromTableId,
              };
            }

            return {
              kind: "user",
              userId: row.userId || row._id,
              fromTableId,
            };
          })
          .filter(Boolean),
    );

    if (!entryMoves.length) return;

    const pendingCut = cutPlayers;
    const moving = dispatch(movePlayers({ entryMoves, toTableId, tournamentId }));

    dispatch(resetHighlights());
    dispatch(resetSelectedPlayers());
    dispatch(clearCutPlayers());

    // Refused (e.g. that table is full): the cut players come back, so they
    // can be pasted on another table without selecting them again.
    const result = await moving;
    if (movePlayers.rejected.match(result)) dispatch(setCutPlayers(pendingCut));
  };

  const handleResetFilters = () => {
    setTableTypeFilter([]);
    setSetNumberFilter([]);
    setNotFullOnlyFilter(false);
    setWithoutNotificationOnlyFilter(false);
    setTimeRangeStart(defaultRangeStartValue);
    setTimeRangeEnd(defaultRangeEndValue);
    setIsTimeRangeFilterApplied(false);
  };

  const handleRefreshTables = () => {
    const syncCursor = syncCursorsByKey?.[syncKey] || "";
    const knownTableIds = tables
      .map((table) => String(table?._id || ""))
      .filter(Boolean);

    if (!syncCursor) {
      fetchedTableIdsRef.current = new Set();
      dispatch(
        fetchTournamentTablesById({
          tournamentId,
          filters: fetchFilters,
          syncKey,
        }),
      );
      return;
    }

    dispatch(
      syncTournamentTablesById({
        tournamentId,
        filters: fetchFilters,
        since: syncCursor,
        knownTableIds,
      syncKey,
      }),
    );
  };

  const handleToggleTimelineSplitView = useCallback(() => {
    setIsTimelineSplitView((prev) => !prev);
  }, []);

  const handleSplitDividerPointerDown = useCallback(
    (event) => {
      if (!isTimelineSplitView) return;

      event.preventDefault();
      dividerDragStateRef.current.active = true;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    },
    [isTimelineSplitView],
  );

  const handleSplitDividerKeyDown = useCallback((event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    setTimelineSplitPercent((prev) =>
      clampTimelineSplitPercent(
        prev + (event.key === "ArrowRight" ? 2 : -2),
      ),
    );
  }, []);

  const handleMoveContextEntryToStage = async (stage) => {
    if (contextMenu.type !== "player") return;
    if (!contextMenu.data?.tableId || !contextMenu.data?.playerId) return;

    const kind = contextMenu.data?.playerKind === "squad" ? "squad" : "user";
    const payload = {
      tournamentId,
      fromTableId: contextMenu.data.tableId,
      kind,
      userId: kind === "user" ? contextMenu.data.playerId : null,
      squadId: kind === "squad" ? contextMenu.data.playerId : null,
    };

    if (stage === "nextStage") {
      await dispatch(moveEntryToNextStage(payload));
      return;
    }

    await dispatch(moveEntryToFinalStage(payload));
  };

  const canMoveContextEntryToNextStage =
    contextMenu.type === "player" &&
    contextTable?.tableType === "qualification";
  const canMoveContextEntryToFinal =
    contextMenu.type === "player" &&
    ["qualification", "nextStage"].includes(contextTable?.tableType);

  const renderTablesContent = (extraClassName = "") => (
    <div
      className={`${styles.tablesContainer} ${extraClassName}`.trim()}
      ref={containerRef}
    >
      <div className={styles.tablesTrack}>
        {groupedTables.map((group) => {
          const isSharedTimeGroup =
            Boolean(group.sharedTimeKey) && group.tables.length > 1;
          const showGroupHeader =
            Boolean(group.sharedTimeKey) && Boolean(group.timeLabel);

          return (
            <div
              key={group.id}
              className={`${styles.tableGroup} ${
                isSharedTimeGroup ? styles.tableGroupShared : ""
              }`}
            >
              {showGroupHeader ? (
                <div className={styles.tableGroupHeader}>
                  <div className={styles.tableGroupMeta}>
                    <div className={styles.tableGroupTime}>
                      {`موعد الجداول: ${group.timeLabel}`}
                    </div>
                    <div className={styles.tableGroupTime}>
                      {`رقم السطر في الجدول الزمني: ${group.timelineRowNumber}`}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className={styles.tableGroupTables}>
                {group.tables.map((table) => {
                  const normalizedSetNumber =
                    String(table?.timeline?.setNumber ?? "").trim() ||
                    "__unset__";
                  const executorNames =
                    normalizedSetNumber === "__unset__"
                      ? []
                      : coLeaderNamesBySetNumber[normalizedSetNumber] || [];
                  const setNumberTheme =
                    table?.tableType === "qualification"
                      ? getSetNumberColorTheme(
                          normalizedSetNumber,
                          setNumberColorPalette,
                        )
                      : null;

                  return (
                    <div
                      key={table._id}
                      ref={(node) => observe(table._id, node)}
                      className={styles.tableSlot}
                    >
                      <div
                        className={styles.tableSetNumber}
                        style={
                          setNumberTheme
                            ? {
                                backgroundColor: setNumberTheme.backgroundColor,
                                borderColor: setNumberTheme.borderColor,
                                color: setNumberTheme.textColor,
                              }
                            : undefined
                        }
                      >
                        {`منفذ: ${executorNames.length ? executorNames.join("، ") : "-"}`}
                      </div>
                      {visibleIds.has(table._id) && (
                        <OneTable
                          tournamentId={tournamentId}
                          tournamentName={name}
                          tableId={table._id}
                          type={table.tableType}
                          timeline={table.timeline}
                          query={query}
                          selectedIds={selectedIds}
                          gameName={gameName}
                          tournamentType={tournamentType}
                          setNumberColorTheme={setNumberTheme}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderTimelinePaneContent = () => {
    if (isTimelineLoading) {
      return (
        <div className={styles.timelinePaneState}>
          <Loader />
        </div>
      );
    }

    if (!hasTimelineCache && timelinesStatus === "failed") {
      return (
        <div className={styles.timelinePaneState}>
          <TextWrapper textStyle="textMid">
            {timelinesError || "تعذر تحميل الجدول الزمني حالياً."}
          </TextWrapper>
        </div>
      );
    }

    if (!timelineData.length) {
      return (
        <div className={styles.timelinePaneState}>
          <TextWrapper textStyle="textMid">
            لا توجد بيانات جدول زمني لعرضها حالياً.
          </TextWrapper>
        </div>
      );
    }

    return (
      <TimelineTable
        timelineData={timelineData}
        tournamentId={tournamentId}
        autoScrollToRight
      />
    );
  };

  const timelinePaneBasis = `calc((100% - ${TIMELINE_SPLIT_DIVIDER_WIDTH_PX}px) * ${
    timelineSplitPercent / 100
  })`;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={styles.page}>
        <div
          className={`${styles.headerSection} ${
            isHeaderCollapsed ? styles.headerSectionCollapsed : ""
          }`}
        >
          <div className={styles.createNewTableContianer}>
            <div className={styles.createNewTableWrapper}>
              <div className={styles.headerControlsWrapper}>
                {(canCreateTables || canSetupNextStage) && (
                  <div className={styles.createButtonsWrapper}>
                    {canCreateTables && (
                      <div className={styles.createMenuWrapper}>
                        <button className={styles.createMenuTrigger} type="button">
                          <TextWrapper textStyle="textMid">
                            انشاء جدول جديد
                          </TextWrapper>
                        </button>

                        <div className={styles.createMenuDropdown}>
                          <button
                            type="button"
                            className={`${styles.createMenuItem} ${styles.createMenuFinal}`}
                            onClick={() => handleCreateNewTable("final")}
                          >
                            انشاء جدول نهائيات
                          </button>
                          <button
                            type="button"
                            className={`${styles.createMenuItem} ${styles.createMenuQualification}`}
                            onClick={() => handleCreateNewTable("qualification")}
                          >
                            انشاء جدول تصفيات
                          </button>
                          <button
                            type="button"
                            className={`${styles.createMenuItem} ${styles.createMenuNextStage}`}
                            onClick={() => handleCreateNewTable("nextStage")}
                          >
                            انشاء جدول المرحلة التالية
                          </button>
                        </div>
                      </div>
                    )}
                    {canSetupNextStage && (
                      <button
                        type="button"
                        className={styles.nextStageSetupTrigger}
                        onClick={() => setShowNextStageSetupModal(true)}
                      >
                        تجهيز المرحلة المتقدمة
                      </button>
                    )}
                  </div>
                )}
                <div className={styles.titleSearchWrapper}>
                  <div className={styles.tournamentNameWrapper}>
                    <TextWrapper textStyle={"textTitle"}>{name}</TextWrapper>
                  </div>
                  <div className={styles.headerMiddle}>
                    {" "}
                    <div className={styles.filterWrapper} data-no-select-box>
                      <button type="button" className={styles.filterTrigger}>
                        فلاتر
                      </button>

                      <div className={styles.filterDropdown}>
                        <div className={styles.filterGroup}>
                          <span className={styles.filterLabel}>نوع الجدول</span>
                          <FilterChips
                            allLabel="كل الانواع"
                            options={visibleTableTypeOptions.filter((option) => option.value !== "all")}
                            selected={tableTypeFilter}
                            onChange={setTableTypeFilter}
                          />
                        </div>

                        <div className={styles.filterGroup}>
                          <span className={styles.filterLabel}>رقم العامود</span>
                          <FilterChips
                            allLabel="كل الأعمدة"
                            options={persistedSetNumberOptions.map((setNumber) => ({
                              value: String(setNumber),
                              label: String(setNumber),
                            }))}
                            selected={setNumberFilter}
                            onChange={setSetNumberFilter}
                          />
                        </div>

                        <div className={styles.filterGroup}>
                          <label className={styles.filterLabel}>المدى الزمني</label>
                          <div className={styles.filterTimeInputs}>
                            <input
                              type="datetime-local"
                              className={styles.filterTimeInput}
                              value={timeRangeStart}
                              min={
                                timelineMinStartMs
                                  ? toDateTimeLocalValue(timelineMinStartMs)
                                  : undefined
                              }
                              max={
                                timeRangeEnd ||
                                (timelineMaxEndMs
                                  ? toDateTimeLocalValue(timelineMaxEndMs)
                                  : undefined)
                              }
                              onChange={(e) => {
                                setIsTimeRangeFilterApplied(true);
                                setTimeRangeStart(e.target.value);
                              }}
                            />
                            <input
                              type="datetime-local"
                              className={styles.filterTimeInput}
                              value={timeRangeEnd}
                              min={
                                timeRangeStart ||
                                (timelineMinStartMs
                                  ? toDateTimeLocalValue(timelineMinStartMs)
                                  : undefined)
                              }
                              max={
                                timelineMaxEndMs
                                  ? toDateTimeLocalValue(timelineMaxEndMs)
                                  : undefined
                              }
                              onChange={(e) => {
                                setIsTimeRangeFilterApplied(true);
                                setTimeRangeEnd(e.target.value);
                              }}
                            />
                          </div>
                        </div>

                        <div className={styles.filterGroup}>
                          <label className={styles.filterLabel}>فلاتر اضافية</label>
                          <label className={styles.filterCheckboxRow}>
                            <input
                              type="checkbox"
                              checked={notFullOnlyFilter}
                              onChange={(e) =>
                                setNotFullOnlyFilter(e.target.checked)
                              }
                            />
                            <span>الجدول غير ممتلئ فقط</span>
                          </label>
                          <label className={styles.filterCheckboxRow}>
                            <input
                              type="checkbox"
                              checked={withoutNotificationOnlyFilter}
                              onChange={(e) =>
                                setWithoutNotificationOnlyFilter(e.target.checked)
                              }
                            />
                            <span>لم يتم ارسال اشعار موعد له</span>
                          </label>
                        </div>

                        <button
                          type="button"
                          className={styles.filterResetBtn}
                          onClick={handleResetFilters}
                        >
                          إعادة ضبط الفلاتر
                        </button>
                      </div>
                    </div>
                    {canManagePlayers && (
                      <div
                        className={styles.transderButton}
                        onClick={() => setShowTransferModal(true)}
                      >
                        <TransferIcon className={styles.transferIcon} />
                      </div>
                    )}
                    <TextInput
                      height="50px"
                      width="min(420px, 78vw)"
                      borderRadius="10px"
                      bordered
                      placeHolder={
                        tournamentType === "squad"
                          ? "البحث عن فريق في البطولة"
                          : "البحث عن لاعب في البطولة"
                      }
                      bgColor={"white"}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.headerActions}>
                  <button
                    type="button"
                    className={`${styles.timelineSplitToggle} ${
                      isTimelineSplitView ? styles.timelineSplitToggleActive : ""
                    }`}
                    onClick={handleToggleTimelineSplitView}
                  >
                    {isTimelineSplitView
                      ? "إخفاء الجدول الزمني"
                      : "عرض الجدول الزمني"}
                  </button>
                  <button
                    type="button"
                    className={styles.refreshButton}
                    onClick={handleRefreshTables}
                    title="تحديث الجداول"
                    aria-label="تحديث الجداول"
                  >
                    <RefreshIcon className={styles.refreshWrapper} />
                  </button>

                  <div className={styles.helpWrapper} data-no-select-box>
                    <button
                      type="button"
                      className={styles.helpTrigger}
                      aria-label="table options help"
                      title="شرح أزرار الجداول"
                    >
                      ?
                    </button>

                    <div className={styles.helpTooltip}>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowNotification}`}
                      >
                        <NotificationIcon className={styles.helpIcon} />
                        <span>ارسال اشعار للجدول</span>
                      </div>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowAddRoomInformation}`}
                      >
                        <AddRoomInformationIcon className={styles.helpIcon} />
                        <span>إضافة معلومات الروم للجدول (مع إشعار للاعبين)</span>
                      </div>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowEditRoomInformation}`}
                      >
                        <EditRoomInformationIcon className={styles.helpIcon} />
                        <span>تعديل معلومات الروم (بدون إشعار)</span>
                      </div>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowAddRoomTime}`}
                      >
                        <AddRoomTimeIcon className={styles.helpIcon} />
                        <span>
                          إضافة أو تعديل موعد الروم اليدوي (يمكن إضافته قبل معلومات
                          الروم، ويرسل إشعار عند الإضافة فقط ويتعطل بعد الإشعار
                          التلقائي)
                        </span>
                      </div>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowNotificationSent}`}
                      >
                        <NotificationSentIcon className={styles.helpIcon} />
                        <span>تم ارسال اشعار الموعد</span>
                      </div>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowShowAll}`}
                      >
                        <ShowAllEntriesIcon className={styles.helpIcon} />
                        <span>عرض جميع المشاركين في الجدول</span>
                      </div>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowDelete}`}
                      >
                        <DeleteActionIcon className={styles.helpIcon} />
                        <span>حذف الجدول</span>
                      </div>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowAddEntry}`}
                      >
                        <AddEntityIcon className={styles.helpIcon} />
                        <span>
                          {tournamentType === "squad" ? "اضافة فريق" : "اضافة لاعب"}
                        </span>
                      </div>
                      <div
                        className={`${styles.helpRow} ${styles.helpRowRefresh}`}
                      >
                        <RefreshIcon className={styles.helpRefreshIcon} />
                        <span>تحديث بيانات الجدول</span>
                      </div>

                      <div className={styles.helpDivider} />
                      <div className={styles.helpSectionTitle}>
                        الوان انواع الجداول
                      </div>

                      <div className={styles.helpColorRow}>
                        <span>تصفيات</span>
                        <span
                          aria-hidden="true"
                          className={`${styles.helpColorSwatch} ${styles.helpColorQualification}`}
                        />
                      </div>
                      <div className={styles.helpColorRow}>
                        <span>متقدمة</span>
                        <span
                          aria-hidden="true"
                          className={`${styles.helpColorSwatch} ${styles.helpColorNextStage}`}
                        />
                      </div>
                      <div className={styles.helpColorRow}>
                        <span>نهائيات</span>
                        <span
                          aria-hidden="true"
                          className={`${styles.helpColorSwatch} ${styles.helpColorFinal}`}
                        />
                      </div>
                      <div className={styles.helpColorRow}>
                        <span>الفائزين</span>
                        <span
                          aria-hidden="true"
                          className={`${styles.helpColorSwatch} ${styles.helpColorWinners}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.headerHandleRow}>
            {isHeaderCollapsed && (
              <button
                type="button"
                className={styles.headerRefresh}
                onClick={handleRefreshTables}
                title="تحديث الجداول"
                aria-label="تحديث الجداول"
              >
                <RefreshIcon className={styles.headerRefreshIcon} />
              </button>
            )}
            <button
              type="button"
              className={styles.headerHandle}
              onClick={toggleHeaderCollapsed}
              aria-expanded={!isHeaderCollapsed}
              aria-label={isHeaderCollapsed ? "إظهار أدوات الجداول" : "إخفاء أدوات الجداول"}
              title={isHeaderCollapsed ? "إظهار أدوات الجداول" : "إخفاء أدوات الجداول"}
            >
              <ChevronDownIcon className={styles.headerHandleIcon} />
            </button>
          </div>
        </div>

        <div className={styles.contentSection}>
          {isTimelineSplitView ? (
            <div className={styles.splitViewContent} ref={splitViewRef}>
              <div
                className={styles.timelineSplitPane}
                style={{ flex: `0 0 ${timelinePaneBasis}` }}
              >
                {renderTimelinePaneContent()}
              </div>

              <div
                className={styles.splitViewDivider}
                role="separator"
                aria-orientation="vertical"
                aria-label="تغيير عرض الجدول الزمني والجداول"
                tabIndex={0}
                onPointerDown={handleSplitDividerPointerDown}
                onKeyDown={handleSplitDividerKeyDown}
              >
                <span className={styles.splitViewDividerGrip} />
              </div>

              <div
                className={styles.tablesSplitPane}
              >
                {renderTablesContent(styles.tablesContainerSplit)}
              </div>
            </div>
          ) : (
            <div className={styles.tablesFullWidthPane}>
              {renderTablesContent()}
            </div>
          )}
        </div>
      </div>

      <SelectionBox boxStyles={boxStyles} selectedCount={selectedCount} />
      {contextMenu.visible && (
        <Portal>
          <div
            className={styles.contextMenu}
            style={{
              position: "fixed",
              top: contextMenu.y,
              left: contextMenu.x,
              zIndex: 4,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {contextMenu.type === "player" && (
              <>
                {canMoveContextEntryToNextStage && (
                  <div
                    className={styles.contextMenuBtn}
                    onClick={() => handleMoveContextEntryToStage("nextStage")}
                  >
                    <TextWrapper textStyle={"textSmall"}>
                      {remainingNextStageNeededFromContextTable === null
                        ? "نقل إلى المرحلة المتقدمة"
                        : `نقل إلى المرحلة المتقدمة (${remainingNextStageNeededFromContextTable})`}
                    </TextWrapper>
                    <TransferIcon className={styles.contextMenuActionIcon} />
                  </div>
                )}
                {canMoveContextEntryToFinal && (
                  <div
                    className={styles.contextMenuBtn}
                    onClick={() => handleMoveContextEntryToStage("final")}
                  >
                    <TextWrapper textStyle={"textSmall"}>
                      نقل إلى النهائيات
                    </TextWrapper>
                    <TransferIcon className={styles.contextMenuActionIcon} />
                  </div>
                )}
                <div
                  className={styles.contextMenuBtn}
                  onClick={handleDeletePlayer}
                >
                  <TextWrapper textStyle={"textSmall"}>
                    {contextMenu.data?.playerKind === "squad"
                      ? "حذف الفريق"
                      : "حذف اللاعب"}
                  </TextWrapper>
                  <img src={deleteImage} alt="recycle bin" />
                </div>
              </>
            )}
            {contextMenu.type === "empty" && (
              <>
                <div className={styles.contextMenuBtn} onClick={handleCut}>
                  <img src={cutImage} alt="scissors" />
                  <TextWrapper textStyle={"textSmall"}>قص</TextWrapper>
                </div>
                <div className={styles.contextMenuBtn} onClick={handlePaste}>
                  <img src={pasteImage} alt="paste" />
                  <TextWrapper textStyle={"textSmall"}>لصق</TextWrapper>
                </div>
              </>
            )}
          </div>
        </Portal>
      )}
      {(fetchStatus === "loading" ||
        syncStatus === "loading" ||
        addStatus === "loading" ||
        createNextStageStatus === "loading" ||
        Object.values(status).some((ele) => ele === "loading")) && (
        <div className={styles.loaderContainer}>
          <Loader size={"large"} />
        </div>
      )}
      {showNextStageSetupModal && (
        <Dialog
          title="تجهيز المرحلة المتقدمة"
          size="sm"
          onClose={() => setShowNextStageSetupModal(false)}
          closeOnEscape
          footer={
            <>
              <DialogButton variant="secondary" onClick={() => setShowNextStageSetupModal(false)}>
                إلغاء
              </DialogButton>
              <DialogButton variant="primary" onClick={handleCreateNextStageTables}>
                تنفيذ
              </DialogButton>
            </>
          }
        >
          <div className={styles.nextStageModalContent}>
            <div className={styles.nextStageModalFields}>
              <label className={styles.nextStageModalField}>
                <span>عدد الجداول</span>
                <input
                  type="number"
                  min={1}
                  value={nextStageTargetTableCount}
                  onChange={(e) => setNextStageTargetTableCount(e.target.value)}
                />
              </label>
              <label className={styles.nextStageModalField}>
                <span>سعة كل جدول</span>
                <select
                  value={nextStagePlayersLimit}
                  disabled={isPubgTournament}
                  onChange={(e) => setNextStagePlayersLimit(e.target.value)}
                >
                  {isPubgTournament ? (
                    <option value={100}>100</option>
                  ) : (
                    <>
                      <option value={60}>60</option>
                      <option value={48}>48</option>
                    </>
                  )}
                </select>
              </label>
            </div>
          </div>
        </Dialog>
      )}
      {showFinalTableModal && (
        <Dialog
          title="انشاء جدول نهائيات"
          size="sm"
          onClose={() => setShowFinalTableModal(false)}
          closeOnEscape
          footer={
            <>
              <DialogButton variant="secondary" onClick={() => setShowFinalTableModal(false)}>
                إلغاء
              </DialogButton>
              <DialogButton variant="primary" onClick={handleConfirmFinalTableCreation}>
                متابعة
              </DialogButton>
            </>
          }
        >
          <div className={styles.nextStageModalContent}>
            <div className={styles.nextStageModalFields}>
              <label className={styles.nextStageModalField}>
                <span>عدد اللاعبين في الجدول</span>
                <input
                  type="number"
                  min={1}
                  value={finalTablePlayersLimit}
                  onChange={(e) => setFinalTablePlayersLimit(e.target.value)}
                />
              </label>
            </div>
          </div>
        </Dialog>
      )}
      {showTransferModal && (
        <TransferModal
          closeModalHandler={() => setShowTransferModal(false)}
          tournamentType={tournamentType}
        />
      )}
    </DndProvider>
  );
};

export default OneTournamentTables;
